/**
 * Series statistics for chart range bars (plan §3.3).
 * Synthetic 52-week hi/lo use the provided bar window's min/max.
 *
 * Provenance: this intentionally REPLACES the demo's formula
 * (`yLo = price * (1 + dd['1Y'])`, `yHi = max(hi, price * 1.04)`,
 * demos/Meridian Terminal.dc.html line 1006). analysis.md FR-D3 and
 * data-flow.md §156 re-specify the 52w range as stats over the full bar set for
 * live data, and the plan outranks the demo — so a diff against the demo's stock
 * screen showing different range-bar endpoints is expected, not a port bug.
 */
import type { SeriesStats } from '@meridian/contracts';

export function seriesStats(bars: readonly number[]): SeriesStats {
  const first = bars[0];
  const last = bars[bars.length - 1];
  if (first === undefined || last === undefined) {
    throw new Error('seriesStats: empty bars');
  }
  /* Fail closed on non-finite input: `b < a` and `b > a` are both false against
     NaN, so a single NaN bar would survive the reducers and surface as `null`
     after JSON.stringify — a chart silently losing its scale, three layers from
     the cause. Unreachable from generateSeries; reachable from live API bars. */
  if (!bars.every((b) => Number.isFinite(b))) {
    throw new Error('seriesStats: non-finite bar');
  }
  const min = bars.reduce((a, b) => (b < a ? b : a), first);
  const max = bars.reduce((a, b) => (b > a ? b : a), first);
  return { first, last, min, max, hi52w: max, lo52w: min };
}
